import { ErrorBox, Field, Input, Link, navigate, useSearch } from '@elsa/ui';
import { type FormEvent, type ReactNode, useEffect, useState } from 'react';
import { BusyButton, InlineLoading } from '../app/loading.tsx';
import { TURNSTILE_SITE_KEY } from '../config.ts';
import { friendly, supabase } from '../lib/supabase.ts';
import { Turnstile } from './Turnstile.tsx';

// Autentificarea Elsei (Supabase Auth): intrare, inregistrare (confirmare pe email, Turnstile),
// resetarea parolei. `next` = unde te intoarce dupa intrare (ex. o invitatie).

export function AuthCard({ title, children }: { title: string; children: ReactNode }) {
  return (
    <div className="flex justify-center px-4 py-12 sm:py-16">
      <div className="w-full max-w-sm space-y-5 border-2 border-cerneala bg-hartie p-6">
        <h1 className="text-[28px] leading-tight font-extrabold tracking-tight">
          {title}
          <span className="text-semnal">.</span>
        </h1>
        {children}
      </div>
    </div>
  );
}

function safeNext(v: string | null): string {
  return v?.startsWith('/') && !v.startsWith('//') ? v : '/app';
}

export function AuthPage() {
  const search = useSearch();
  const mode = search.get('mod') ?? 'intrare';
  const next = safeNext(search.get('next'));
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);
  const [captcha, setCaptcha] = useState<string | null>(null);

  const go = (m: string) => {
    setError(null);
    setDone(null);
    navigate(
      `/autentificare?mod=${m}${search.get('next') ? `&next=${encodeURIComponent(next)}` : ''}`,
    );
  };

  const run = (fn: (f: FormData) => Promise<void>) => async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const f = new FormData(e.currentTarget);
    setBusy(true);
    setError(null);
    try {
      await fn(f);
    } catch (err) {
      setError(new Error(friendly((err as Error).message)));
    } finally {
      setBusy(false);
    }
  };

  if (mode === 'inregistrare') {
    return (
      <AuthCard title="Cont nou">
        {done ? (
          <p className="text-sm">{done}</p>
        ) : (
          <form
            className="space-y-3"
            onSubmit={run(async (f) => {
              const { data, error: e } = await supabase.auth.signUp({
                email: String(f.get('email')).trim(),
                password: String(f.get('password')),
                options: {
                  data: { full_name: String(f.get('full_name')).trim() },
                  emailRedirectTo: `${location.origin}${next}`,
                  ...(captcha ? { captchaToken: captcha } : {}),
                },
              });
              if (e) throw e;
              if (data.session) navigate(next);
              else
                setDone(
                  'Ți-am trimis un email de confirmare. Deschide linkul din el ca să intri în cont.',
                );
            })}
          >
            <Field label="Nume și prenume">
              <Input name="full_name" autoComplete="name" required minLength={2} maxLength={120} />
            </Field>
            <Field label="Email de serviciu">
              <Input name="email" type="email" autoComplete="email" required />
            </Field>
            <Field label="Parolă" help="Cel puțin 8 caractere.">
              <Input
                name="password"
                type="password"
                autoComplete="new-password"
                required
                minLength={8}
              />
            </Field>
            <label className="flex items-start gap-2 text-xs text-gri-800">
              <input type="checkbox" name="terms" required className="mt-0.5" />
              <span>
                Accept{' '}
                <Link to="/termeni" className="underline">
                  termenii
                </Link>{' '}
                și am citit{' '}
                <Link to="/confidentialitate" className="underline">
                  politica de confidențialitate
                </Link>
                .
              </span>
            </label>
            {TURNSTILE_SITE_KEY ? (
              <Turnstile siteKey={TURNSTILE_SITE_KEY} onToken={setCaptcha} />
            ) : null}
            <ErrorBox error={error} />
            <BusyButton
              tone="primary"
              type="submit"
              busy={busy}
              disabled={Boolean(TURNSTILE_SITE_KEY && !captcha)}
              className="w-full"
            >
              Creează contul
            </BusyButton>
          </form>
        )}
        <p className="text-center text-sm">
          Ai deja cont?{' '}
          <button type="button" className="underline" onClick={() => go('intrare')}>
            Intră
          </button>
        </p>
      </AuthCard>
    );
  }

  if (mode === 'resetare') {
    return (
      <AuthCard title="Resetează parola">
        {done ? (
          <p className="text-sm">{done}</p>
        ) : (
          <form
            className="space-y-3"
            onSubmit={run(async (f) => {
              const { error: e } = await supabase.auth.resetPasswordForEmail(
                String(f.get('email')).trim(),
                {
                  redirectTo: `${location.origin}/parola-noua`,
                  ...(captcha ? { captchaToken: captcha } : {}),
                },
              );
              if (e) throw e;
              setDone('Dacă adresa are cont, ți-am trimis un link pentru parola nouă.');
            })}
          >
            <Field label="Email">
              <Input name="email" type="email" autoComplete="email" required />
            </Field>
            {TURNSTILE_SITE_KEY ? (
              <Turnstile siteKey={TURNSTILE_SITE_KEY} onToken={setCaptcha} />
            ) : null}
            <ErrorBox error={error} />
            <BusyButton tone="primary" type="submit" busy={busy} className="w-full">
              Trimite linkul
            </BusyButton>
          </form>
        )}
        <button
          type="button"
          className="w-full text-center text-sm underline"
          onClick={() => go('intrare')}
        >
          Înapoi la intrare
        </button>
      </AuthCard>
    );
  }

  return (
    <AuthCard title="Intră în cont">
      <form
        className="space-y-3"
        onSubmit={run(async (f) => {
          const { error: e } = await supabase.auth.signInWithPassword({
            email: String(f.get('email')).trim(),
            password: String(f.get('password')),
            ...(captcha ? { options: { captchaToken: captcha } } : {}),
          });
          if (e) throw e;
          navigate(next);
        })}
      >
        <Field label="Email">
          <Input name="email" type="email" autoComplete="username" required />
        </Field>
        <Field label="Parolă">
          <Input name="password" type="password" autoComplete="current-password" required />
        </Field>
        {TURNSTILE_SITE_KEY ? (
          <Turnstile siteKey={TURNSTILE_SITE_KEY} onToken={setCaptcha} />
        ) : null}
        <ErrorBox error={error} />
        <BusyButton tone="primary" type="submit" busy={busy} className="w-full">
          Intră
        </BusyButton>
      </form>
      <div className="flex justify-between text-sm">
        <button type="button" className="underline" onClick={() => go('resetare')}>
          Am uitat parola
        </button>
        <button type="button" className="underline" onClick={() => go('inregistrare')}>
          Cont nou
        </button>
      </div>
    </AuthCard>
  );
}

// Linkul din emailul de resetare deschide /parola-noua cu sesiunea de recuperare in URL
// (supabase-js o preia singur: detectSessionInUrl).
export function NewPasswordPage() {
  const [ready, setReady] = useState<boolean | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    const { data } = supabase.auth.onAuthStateChange((event, s) => {
      if (event === 'PASSWORD_RECOVERY' || s) setReady(true);
    });
    const t = setTimeout(() => {
      void supabase.auth.getSession().then(({ data: d }) => setReady(Boolean(d.session)));
    }, 1500);
    return () => {
      clearTimeout(t);
      data.subscription.unsubscribe();
    };
  }, []);
  return (
    <AuthCard title="Parolă nouă">
      {ready === false ? (
        <p className="text-sm">
          Linkul a expirat sau a fost deja folosit.{' '}
          <Link to="/autentificare?mod=resetare" className="underline">
            Cere unul nou
          </Link>
          .
        </p>
      ) : (
        <form
          className="space-y-3"
          onSubmit={async (e) => {
            e.preventDefault();
            const f = new FormData(e.currentTarget);
            setBusy(true);
            setError(null);
            const { error: err } = await supabase.auth.updateUser({
              password: String(f.get('password')),
            });
            setBusy(false);
            if (err) setError(new Error(friendly(err.message)));
            else navigate('/app');
          }}
        >
          <Field label="Parola nouă" help="Cel puțin 8 caractere.">
            <Input
              name="password"
              type="password"
              autoComplete="new-password"
              required
              minLength={8}
            />
          </Field>
          <ErrorBox error={error} />
          {ready === null ? <InlineLoading>Verificăm linkul din email…</InlineLoading> : null}
          <BusyButton tone="primary" type="submit" busy={busy} disabled={!ready} className="w-full">
            Salvează parola
          </BusyButton>
        </form>
      )}
    </AuthCard>
  );
}
